import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronRight, Loader2, BrainCircuit, Sparkles, Upload, Download, GitCompareArrows, SearchX } from "lucide-react";
import { useListControls, ListToolbar, ListPagination } from "@/components/ListControls";

const scoreColor = (s) => (s >= 70 ? "text-[#00A859]" : s >= 40 ? "text-[#F59E0B]" : "text-destructive");
const emptyForm = { client_name: "", sector: "", contact: "", notes: "", framework_id: "default", scores: {}, area_notes: {} };

export default function ReadinessPage() {
  const [items, setItems] = useState([]);
  const [framework, setFramework] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [aiNotes, setAiNotes] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { canEdit } = useAuth();

  const controls = useListControls(items, {
    searchFields: ["client_name", "sector", "contact"],
    filters: {
      sector: { label: "Settore", get: (it) => it.sector },
      tier: { label: "Maturità", get: (it) => it.tier?.label },
    },
    sortOptions: [
      { value: "score_desc", label: "Punteggio ↓", cmp: (a, b) => b.overall_score - a.overall_score },
      { value: "score_asc", label: "Punteggio ↑", cmp: (a, b) => a.overall_score - b.overall_score },
      { value: "name", label: "Nome A–Z", cmp: (a, b) => (a.client_name || "").localeCompare(b.client_name || "") },
    ],
    pageSize: 9,
  });

  const load = async () => {
    const [a, f, fl] = await Promise.all([api.get("/readiness"), api.get("/readiness/framework"), api.get("/frameworks")]);
    setItems(a.data);
    setFramework(f.data.areas);
    setFrameworks(fl.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const fetchAreas = async (fid) => {
    const { data } = await api.get(`/readiness/framework?framework_id=${fid}`);
    return data.areas;
  };

  const openNew = async () => {
    const areas = await fetchAreas("default");
    const scores = {};
    areas.forEach((a) => { scores[a.key] = 3; });
    setFramework(areas);
    setForm({ ...emptyForm, framework_id: "default", scores }); setEditId(null); setAiNotes(""); setOpen(true);
  };
  const openEdit = async (it, e) => {
    e.stopPropagation();
    const fid = it.framework_id || "default";
    const areas = await fetchAreas(fid);
    setFramework(areas);
    setForm({ client_name: it.client_name, sector: it.sector || "", contact: it.contact || "", notes: it.notes || "", framework_id: fid, scores: it.scores || {}, area_notes: it.area_notes || {} });
    setEditId(it.id); setAiNotes(""); setOpen(true);
  };

  const changeFramework = async (fid) => {
    const areas = await fetchAreas(fid);
    const scores = {};
    areas.forEach((a) => { scores[a.key] = 3; });
    setFramework(areas);
    setForm((f) => ({ ...f, framework_id: fid, scores, area_notes: {} }));
  };

  const suggestWithAI = async () => {
    if (!aiNotes.trim()) { toast.error("Incolla le note dell'intervista"); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post("/readiness/suggest", { notes: aiNotes, client_name: form.client_name, sector: form.sector, framework_id: form.framework_id });
      setForm((f) => ({ ...f, scores: { ...f.scores, ...(data.scores || {}) }, area_notes: { ...f.area_notes, ...(data.area_notes || {}) }, notes: f.notes || data.summary || "" }));
      toast.success("Punteggi compilati dall'AI — verifica e correggi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/readiness/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} assessment importati` + (data.errors?.length ? `, ${data.errors.length} errori` : ""));
      if (data.errors?.length) data.errors.slice(0, 3).forEach((er) => toast.error(er));
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.client_name.trim()) { toast.error("Il nome cliente è obbligatorio"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/readiness/${editId}`, form);
      else await api.post("/readiness", form);
      toast.success(editId ? "Assessment aggiornato" : "Assessment creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo assessment?")) return;
    await api.delete(`/readiness/${id}`); toast.success("Assessment eliminato"); load();
  };

  return (
    <Layout>
      <PageHeader title="AI & Sales Readiness" subtitle={`${items.length} assessment cliente`}>
        <Button variant="outline" className="rounded-none" data-testid="readiness-compare-btn" onClick={() => navigate("/readiness/compare")}>
          <GitCompareArrows className="w-4 h-4 mr-2" /> Confronto
        </Button>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="readiness-csv-input" />
            <Button variant="outline" className="rounded-none" data-testid="readiness-import-btn" onClick={() => fileRef.current?.click()} disabled={importing}>
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Importa CSV
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => window.open(`${API}/readiness/csv-template`, "_blank")} title="Scarica template CSV">
              <Download className="w-4 h-4" />
            </Button>
            <Button data-testid="add-readiness-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuovo assessment</Button>
          </>
        )}
      </PageHeader>

      <div className="p-8">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <BrainCircuit className="w-10 h-10 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4">Nessun assessment cliente. Valuta la maturità AI & commerciale su 5 aree pesate.</p>
            {canEdit && <Button onClick={openNew} className="mt-4 rounded-none"><Plus className="w-4 h-4 mr-2" /> Crea il primo assessment</Button>}
          </div>
        ) : (
          <>
            <ListToolbar controls={controls} searchPlaceholder="Cerca cliente, settore, referente…" />
            {controls.filtered.length === 0 ? (
              <div className="border border-dashed border-border bg-white p-16 text-center" data-testid="readiness-no-results">
                <SearchX className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground mt-4">Nessun assessment corrisponde ai filtri.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="readiness-list">
                {controls.visible.map((it, i) => (
              <div key={it.id} data-testid={`readiness-card-${i}`} onClick={() => navigate(`/readiness/${it.id}`)} className="bg-white border border-border p-5 cursor-pointer hover:bg-secondary transition-colors duration-150">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading font-semibold tracking-tight">{it.client_name}</h3>
                    <div className="data-label mt-1">{it.sector || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono font-bold text-3xl ${scoreColor(it.overall_score)}`}>{it.overall_score}</div>
                    <div className="data-label">/100</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {it.tier?.label && <span className="text-[10px] font-bold tracking-wide text-white px-2 py-0.5" style={{ backgroundColor: it.tier.color }}>{it.tier.label}</span>}
                  {it.ai_deliverable ? <span className="text-[10px] font-bold tracking-wide bg-primary text-primary-foreground px-2 py-0.5">DELIVERABLE PRONTO</span> : <span className="data-label">Deliverable non generato</span>}
                  {it.self_assessed && <span className="text-[10px] font-bold tracking-wide bg-secondary text-secondary-foreground px-2 py-0.5">AUTO-COMPILATO</span>}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <div className="flex gap-3">
                    {canEdit && <button onClick={(e) => openEdit(it, e)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                    {canEdit && <button onClick={(e) => remove(it.id, e)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
              </div>
            )}
            <ListPagination controls={controls} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica assessment" : "Nuovo assessment cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Cliente *</Label><Input data-testid="readiness-client-input" className="mt-1 rounded-none" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
              <div><Label className="data-label">Settore</Label><Input className="mt-1 rounded-none" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} /></div>
              <div><Label className="data-label">Referente</Label><Input className="mt-1 rounded-none" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Nome + ruolo" /></div>
            </div>
            <div><Label className="data-label">Note generali</Label><Textarea className="mt-1 rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            <div>
              <Label className="data-label">Framework di assessment</Label>
              <Select value={form.framework_id} onValueChange={changeFramework} disabled={!!editId}>
                <SelectTrigger data-testid="readiness-framework-select" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {frameworks.map((fw) => <SelectItem key={fw.id} value={fw.id}>{fw.name}{fw.is_default ? " (predefinito)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {editId && <p className="text-[10px] text-muted-foreground mt-1">Il framework non è modificabile dopo la creazione.</p>}
            </div>

            <div className="border border-primary/30 bg-primary/5 p-4">
              <div className="flex items-center gap-2 data-label mb-2"><Sparkles className="w-3.5 h-3.5 text-primary" /> Compilazione assistita da AI</div>
              <p className="text-xs text-muted-foreground mb-2">Incolla gli appunti dell'intervista: l'AI proporrà i punteggi delle 5 aree (potrai correggerli).</p>
              <Textarea data-testid="ai-notes-input" className="rounded-none bg-white" rows={4} value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} placeholder="Es. Usano Excel sparsi, nessun CRM, processi a memoria dei venditori, titolare motivato con budget..." />
              <Button type="button" data-testid="ai-suggest-btn" onClick={suggestWithAI} disabled={suggesting} className="rounded-none mt-2" size="sm">
                {suggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Compila con AI
              </Button>
            </div>

            <div className="border-t border-border pt-4 space-y-6">
              <div className="data-label">Scoring per area (1–5)</div>
              {framework.map((a) => (
                <div key={a.key} className="border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{a.name}</span>
                      <span className="text-muted-foreground font-mono text-xs ml-2">peso {a.weight}%</span>
                    </div>
                    <span className="font-mono font-bold text-xl text-primary" data-testid={`readiness-score-${a.key}`}>{form.scores[a.key] ?? 0}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 italic">{a.guide_question}</p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 list-disc pl-4">
                    {a.questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                  <Slider className="mt-3" value={[form.scores[a.key] ?? 0]} min={1} max={5} step={1}
                    onValueChange={([v]) => setForm({ ...form, scores: { ...form.scores, [a.key]: v } })} />
                  <div className="grid grid-cols-3 gap-2 mt-2 text-[10px] text-muted-foreground">
                    <span><b>1</b> {a.anchors["1"]}</span>
                    <span className="text-center"><b>3</b> {a.anchors["3"]}</span>
                    <span className="text-right"><b>5</b> {a.anchors["5"]}</span>
                  </div>
                  <Input className="mt-3 rounded-none text-xs h-8" placeholder="Note per quest'area (opzionale)" value={form.area_notes[a.key] || ""} onChange={(e) => setForm({ ...form, area_notes: { ...form.area_notes, [a.key]: e.target.value } })} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-readiness-btn" className="rounded-none" onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
